import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPhone, FiMail, FiX, FiLock, FiUnlock, FiAlertTriangle, FiCheck } = FiIcons;

const EmergencyModal = ({ isOpen, onClose }) => {
  const [pin, setPin] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [error, setError] = useState(false);

  const CORRECT_PIN = '2026111';

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pin === CORRECT_PIN) {
      setIsUnlocked(true);
      setError(false);
    } else {
      setError(true);
      // Shake effect or error indication could be added here
      setTimeout(() => setError(false), 2000);
    }
  };

  const handleClose = () => {
    setPin('');
    setIsUnlocked(false);
    setError(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
          />
          
          {/* Modal Container */}
          <div className="fixed inset-0 flex items-center justify-center z-[101] pointer-events-none p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden pointer-events-auto border-t-8 border-mk-red-600"
            >
              {/* Header */}
              <div className="bg-gray-50 p-6 border-b border-gray-100 flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <SafeIcon icon={FiAlertTriangle} className="text-mk-red-600 mr-3" />
                    Notfallkontakt
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Schnelle Hilfe bei dringenden Problemen.
                  </p>
                </div>
                <button 
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-200"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-8">
                {/* Standard Kontakt */}
                <div>
                  <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">
                    Allgemeine Erreichbarkeit
                  </h3>
                  <div className="space-y-3">
                    <a href="tel:+4924528607038" className="flex items-center p-3 bg-gray-50 rounded-xl hover:bg-mk-teal-50 transition-colors group">
                      <div className="w-10 h-10 bg-mk-teal-100 text-mk-teal-600 rounded-full flex items-center justify-center mr-4 group-hover:bg-mk-teal-500 group-hover:text-white transition-colors">
                        <SafeIcon icon={FiPhone} className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="block text-gray-900 font-bold">+49 (0) 2452 8607038</span>
                        <span className="text-xs text-gray-500">Bürozeiten: Mo-Fr 7-17 Uhr</span>
                      </div>
                    </a>
                    <a href="mailto:info@ausbau.nrw" className="flex items-center p-3 bg-gray-50 rounded-xl hover:bg-mk-teal-50 transition-colors group">
                      <div className="w-10 h-10 bg-mk-teal-100 text-mk-teal-600 rounded-full flex items-center justify-center mr-4 group-hover:bg-mk-teal-500 group-hover:text-white transition-colors">
                        <SafeIcon icon={FiMail} className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="block text-gray-900 font-bold">info@ausbau.nrw</span>
                        <span className="text-xs text-gray-500">Antwort innerhalb von 24h</span>
                      </div>
                    </a>
                  </div>
                </div>

                {/* VIP / Notfall Bereich */}
                <div className="border-t border-gray-100 pt-6">
                  <h3 className="text-sm font-bold text-mk-red-600 uppercase tracking-wider mb-4 flex items-center">
                    {isUnlocked ? <SafeIcon icon={FiUnlock} className="mr-2" /> : <SafeIcon icon={FiLock} className="mr-2" />}
                    24h Kunden-Notdienst
                  </h3>

                  {!isUnlocked ? (
                    <div className="bg-mk-red-50 p-5 rounded-xl border border-mk-red-100">
                      <p className="text-sm text-gray-700 mb-4">
                        Dieser Bereich ist für Bestandskunden mit aktivem Wartungsvertrag oder in dringenden Notfällen reserviert.
                      </p>
                      <form onSubmit={handlePinSubmit} className="relative">
                        <input
                          type="password"
                          placeholder="Kunden-PIN eingeben"
                          value={pin}
                          onChange={(e) => setPin(e.target.value)}
                          className={`w-full pl-4 pr-12 py-3 rounded-lg border focus:ring-2 focus:outline-none transition-all ${
                            error 
                              ? 'border-red-300 focus:ring-red-200 bg-red-50 text-red-900 placeholder-red-400' 
                              : 'border-gray-300 focus:ring-mk-red-200 focus:border-mk-red-400'
                          }`}
                        />
                        <button 
                          type="submit"
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-mk-red-600 text-white p-1.5 rounded-md hover:bg-mk-red-700 transition-colors"
                        >
                          <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                        </button>
                      </form>
                      {error && (
                        <p className="text-xs text-red-600 mt-2 font-medium flex items-center">
                          <SafeIcon icon={FiAlertTriangle} className="mr-1 w-3 h-3" /> Falsche PIN
                        </p>
                      )}
                    </div>
                  ) : (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-mk-red-600 text-white p-5 rounded-xl shadow-inner"
                    >
                      <div className="flex items-center mb-4 text-mk-red-100 text-sm font-medium">
                        <SafeIcon icon={FiCheck} className="mr-2" /> Zugriff gewährt
                      </div>
                      <div className="space-y-4">
                        <a href="tel:0163000000" className="flex items-center group">
                          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mr-4 group-hover:bg-white/30 transition-colors">
                            <SafeIcon icon={FiPhone} className="w-5 h-5" />
                          </div>
                          <div>
                            <span className="block font-bold text-lg">0163 000000</span>
                            <span className="text-xs text-mk-red-100">Direktwahl Notdienst</span>
                          </div>
                        </a>
                        <a href="mailto:notfall@ausbau.nrw" className="flex items-center group">
                          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mr-4 group-hover:bg-white/30 transition-colors">
                            <SafeIcon icon={FiMail} className="w-5 h-5" />
                          </div>
                          <div>
                            <span className="block font-bold text-lg">notfall@ausbau.nrw</span>
                            <span className="text-xs text-mk-red-100">Priorisierte E-Mail</span>
                          </div>
                        </a>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
              
              <div className="bg-gray-50 px-6 py-4 text-center border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  Missbrauch der Notrufnummer kann zur Kündigung von Serviceverträgen führen.
                </p>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

// Helper Icon needed for input button (imported locally to avoid adding to main imports if not needed elsewhere)
const FiArrowRight = FiIcons.FiArrowRight;

export default EmergencyModal;